import { api, getAccessToken, type AmpCandidates, type AmpExtraction, type AmpTask } from '../api';
import { callout, field, input, opt, openDialog, select, val } from '../dialog';
import { openPdfViewer } from '../dialogs/pdf-viewer';
import { errorCard, esc, fmtDate, fmtHours, html, loading, pill, tile, toast } from '../ui';

/**
 * Reading a maintenance programme out of the aircraft's manuals.
 *
 * The extraction is the easy half. This screen is the hard half, because what
 * it produces is a proposal and somebody has to sign for it — ML.A.302 — and a
 * reviewer who cannot check a task in two seconds will accept it. A
 * rubber-stamped programme is worse than no feature at all: it looks approved.
 *
 * So every row carries the words it was read from and the page they are on,
 * and the three cases that need a decision are made louder than the two
 * hundred that do not: a line that only points at another manual, a task that
 * applies only to fitted equipment, and a task carrying its own interval
 * inside a section with a different one.
 */

const STATE_LABEL: Record<string, string> = {
  PENDING: 'Not started',
  READING: 'Reading the manuals',
  READY: 'Ready to review',
  APPROVED: 'Approved',
  FAILED: 'Failed',
  ABANDONED: 'Abandoned',
};

const money = (n: number) => n.toLocaleString('en-GB');

function sectionInterval(s: AmpExtraction['sections'][number]): string {
  const parts = [
    s.intervalHours ? `${s.intervalHours} h` : null,
    s.intervalMonths ? `${s.intervalMonths} months` : null,
  ].filter(Boolean);
  if (!parts.length) return 'no stated interval';
  return parts.join(' / ') + (parts.length > 1 ? ', whichever first' : '');
}

function taskRow(t: AmpTask): string {
  const flags: string[] = [];
  if (t.isReference) {
    flags.push(`<span class="fl fl-ref">not a task — points at ${esc(t.referencedManual ?? 'another manual')}</span>`);
  }
  if (t.conditionText) flags.push(`<span class="fl fl-cond">only if: ${esc(t.conditionText)}</span>`);
  if (t.ownIntervalMonths) flags.push(`<span class="fl fl-own">own interval — ${t.ownIntervalMonths} months</span>`);
  // Where the manual states its schedule as a grid, what the row is ticked at
  // is the fact to check against the page — and it explains the interval the
  // task ends up with, which is the shortest of them.
  if (t.markedAtHours) {
    flags.push(`<span class="fl fl-own">ticked at ${esc(t.markedAtHours.replace(/,/g, ', '))} h</span>`);
  } else if (t.ownIntervalHours) {
    flags.push(`<span class="fl fl-own">own interval — ${t.ownIntervalHours} h</span>`);
  }
  if (!t.quoteVerified) flags.push('<span class="fl fl-own">quote not found in the manual</span>');
  if (t.applicability === 'ALREADY_PASSED') flags.push('<span class="fl fl-cond">already behind this aircraft</span>');
  if (t.applicability === 'UNKNOWN_HOURS') flags.push('<span class="fl fl-cond">the aircraft’s hours are not on file</span>');
  if (t.fromRole === 'SUPPLEMENT') flags.push('<span class="fl fl-supp">supplement</span>');
  // Reading a second manual against an aircraft that already has a programme
  // proposes a few lines it already holds. Accepting them changes nothing —
  // the existing task keeps its place and its history — but the reviewer
  // should not have to guess that.
  if (t.onProgramme === 'SAME') flags.push('<span class="fl fl-have">already on the programme</span>');
  if (t.onProgramme === 'DIFFERENT_INTERVAL') {
    flags.push('<span class="fl fl-cond">on the programme at a different interval</span>');
  }

  const state = t.isReference
    ? 'is-ref'
    : t.decision === 'REJECTED'
      ? 'is-rejected'
      : t.decision === 'ACCEPTED'
        ? ''
        : 'is-review';

  return html`
    <div class="trow ${state}" data-task="${esc(t.id)}">
      <div class="tsel">
        <input type="checkbox" data-accept="${esc(t.id)}"
               ${t.decision === 'ACCEPTED' ? 'checked' : ''} ${t.isReference ? 'disabled' : ''}
               aria-label="Accept this task">
      </div>
      <div class="tbody">
        <div class="ttitle">${esc(t.title)}</div>
        <blockquote class="tquote">${esc(t.verbatim)}</blockquote>
        <div class="tflags">
          ${t.attachmentId && t.pageNumber
            ? html`<a class="cite" href="#" data-cite="${esc(t.attachmentId)}" data-page="${t.pageNumber}"
                      data-doc="${esc(t.fromDocument ?? 'the manual')}">${esc(t.fromDocument ?? 'manual')} · p.${t.pageNumber}</a>`
            : html`<span class="cite" style="opacity:.6">${esc(t.fromDocument ?? 'manual')}</span>`}
          ${flags.join('')}
        </div>
        ${t.applyNote ? html`<div class="tnote">${esc(t.applyNote)}</div>` : ''}
      </div>
      <div class="tact">
        ${t.isReference
          ? ''
          : html`<button class="btn sm" data-edit="${esc(t.id)}">Edit</button>
                 <button class="btn sm" data-reject="${esc(t.id)}">${t.decision === 'REJECTED' ? 'Restore' : 'Reject'}</button>`}
      </div>
    </div>
  `;
}

// ── the screens ─────────────────────────────────────────────────────────────

/**
 * What a reading in progress looks like.
 *
 * The reading used to be the HTTP request: the button said "Reading…" and the
 * page waited for it to come back. Five manuals take longer than the five
 * minutes Node allows a request to live, so the answer arrived at a socket
 * nobody was holding and the button said "Reading…" for ever, while the
 * server quietly finished and marked the proposal ready.
 *
 * So the page follows the work instead of holding it: one manual at a time,
 * with what has come back so far, refreshed every few seconds.
 */
function readingCallout(e: AmpExtraction): string {
  const done = e.documents.filter((d) => d.pages !== null).length;
  const total = Math.max(e.totals.documents, e.documents.length, 1);
  const current = e.documents.find((d) => d.pages === null);
  return callout('info', 'i', html`
    <b>Reading manual ${Math.min(done + 1, total)} of ${total}${current ? ` — ${esc(current.title)}` : ''}.</b>
    A long one takes about a minute; the whole set takes several. This page keeps itself up to date, so
    it can be left open — or closed and come back to.
    ${done ? html`<br>${done} read so far · ${e.totals.sections} section(s) · ${e.totals.proposed} task(s).` : ''}
    <br><br>
    <button class="btn sm" data-abandon>Abandon the reading</button>
  `);
}

function pickerView(c: AmpCandidates, last: AmpExtraction | null): string {
  // A reading that failed or is still running used to be invisible here:
  // the screen offered the button, the button refused, and the refusal told
  // the user to abandon something they had no way to reach.
  const inFlight = last && ['PENDING', 'READING'].includes(last.state);
  const failed = last?.state === 'FAILED';
  return html`
    <div class="page-h">
      <div>
        <p class="tiny dim"><a href="/fleet/${esc(c.aircraft.id)}/programme">← ${esc(c.aircraft.registration)}</a></p>
        <h1>Build the programme from the manuals</h1>
        <p class="dim">
          ${esc(c.aircraft.registration)} · ${esc(c.aircraft.typeModel)}${c.aircraft.variant ? ` · ${esc(c.aircraft.variant)}` : ''}
          · ${fmtHours(c.aircraft.tsnHours)} h
          ${c.aircraft.lastInspectionAt ? ` · last inspection ${esc(fmtDate(c.aircraft.lastInspectionAt))}` : ' · no inspection on file'}
        </p>
      </div>
      <button class="btn pri" data-start>Read the ticked manuals</button>
    </div>

    <div data-reading>${inFlight ? readingCallout(last!) : ''}</div>

    ${failed
      ? callout('exceed', '!', html`
          <b>The last reading did not finish.</b><br>
          ${esc(last!.failure ?? 'No reason was recorded.')}
          <br><br>
          <button class="btn sm" data-abandon>Clear it and start again</button>
        `)
      : ''}

    ${!c.configured
      ? callout('caution', '!', html`
          <b>No key is configured on this installation</b>, so the manuals cannot be read. The rest of
          this screen shows what would be read if there were one.
        `)
      : ''}

    ${c.aircraft.tsnHours <= 0
      ? callout('caution', '!', html`
          <b>${esc(c.aircraft.registration)} has no total time on file.</b> Whether the first 25-hour and
          50-hour inspections are still ahead of this aircraft cannot be told from a total of zero, so
          those tasks will come back flagged rather than scheduled. Record the hours first if you have them.
        `)
      : ''}

    <div class="card">
      <div class="card-h">
        <div>
          <h2>Manuals on file</h2>
          <p class="tiny dim">Whether a supplement applies to this airframe is the one judgement the manual cannot make</p>
        </div>
      </div>
      <div>
        ${c.candidates.length
          ? c.candidates.map((d) => html`
              <div class="doc">
                <input type="checkbox" data-doc-pick="${esc(d.documentId)}" ${d.suggested ? 'checked' : ''}>
                <div class="tbody">
                  <div class="dt">${esc(d.title)}</div>
                  <div class="dm">${esc(d.fileName)} · ${(d.byteSize / 1e6).toFixed(1)} MB</div>
                </div>
                <span class="verdict ${d.suggested ? 'v-yes' : 'v-ask'}">${esc(d.verdict)}</span>
              </div>
            `).join('')
          : html`<div class="doc"><div class="tbody">
              <div class="dt">No maintenance manuals on file</div>
              <div class="dm">Upload the AMM against this aircraft first — Documents.</div>
            </div></div>`}
      </div>
      <div class="card-f">
        Only chapter 5 is read — the time limits and the scheduled maintenance — not the whole manual.
        Reading four manuals takes a few minutes.
      </div>
    </div>
  `;
}

/**
 * A card that folds.
 *
 * The review is a long page — a section per interval, and two hundred tasks
 * spread across them — and every one of them was open at once, so finding the
 * hundred-hour worksheet meant scrolling past the whole lubrication plan. Only
 * the first is open on arrival; the rest state what they hold in their heading
 * so it can be found without opening it.
 *
 * `<details>` rather than a class toggle: keyboard and find-in-page work
 * without anything being written for them.
 *
 * Which ones are open is remembered across renders. Accepting a whole section
 * re-renders the page, and without this the section just accepted folded shut
 * under the reviewer — the same losing-your-place problem the task checkbox
 * avoids by not re-rendering at all.
 */
const openFolds = new Set<string>();
let foldsFor: string | null = null;

/** First arrival at a reading: what was read is open, the rest are not. */
function initialiseFolds(extractionId: string): void {
  if (foldsFor === extractionId) return;
  foldsFor = extractionId;
  openFolds.clear();
  openFolds.add('read');
}

const fold = (opts: { key: string; title: string; sub: string; actions?: string; body: string }): string => html`
  <details class="card" data-fold="${esc(opts.key)}" ${openFolds.has(opts.key) ? 'open' : ''}>
    <summary class="card-h">
      <div class="sum">
        <span class="twist" aria-hidden="true"></span>
        <div>
          <h2>${opts.title}</h2>
          <p class="tiny dim">${opts.sub}</p>
        </div>
      </div>
      ${opts.actions ?? ''}
    </summary>
    ${opts.body}
  </details>
`;

function reviewView(e: AmpExtraction): string {
  const approved = e.state === 'APPROVED';
  initialiseFolds(e.id);
  return html`
    <div class="page-h">
      <div>
        <p class="tiny dim"><a href="/fleet/${esc(e.aircraft.id)}/programme">← ${esc(e.aircraft.registration)}</a></p>
        <h1>Review the proposed programme</h1>
        <p class="dim">
          ${esc(e.aircraft.registration)} · ${fmtHours(e.aircraft.tsnHours)} h
          ${e.readAgainst.countersAsOf ? ` as of ${esc(fmtDate(e.readAgainst.countersAsOf))}` : ''}
          ·
          ${e.readAgainst.lastInspectionAt
            ? `last inspection ${esc(fmtDate(e.readAgainst.lastInspectionAt))}`
            : 'no inspection on file'}
        </p>
        <p class="tiny dim">
          Read from ${e.totals.documents} manual(s) by ${esc(e.model)} ·
          ${money(e.totals.tokensIn)} tokens in, ${money(e.totals.tokensOut)} out
        </p>
      </div>
      <div style="display:flex;gap:8px;align-items:center">
        ${pill(approved ? 'OK' : e.state === 'READY' ? 'DUE_SOON' : 'NA', STATE_LABEL[e.state] ?? e.state)}
        <button class="btn" data-pick>Read other manuals</button>
        ${approved ? '' : '<button class="btn" data-abandon>Abandon</button>'}
        ${approved ? '' : '<button class="btn pri" data-approve>Approve as a programme</button>'}
      </div>
    </div>

    <div class="tiles">
      ${tile({ label: 'Proposed', value: e.totals.proposed, sub: `${e.totals.sections} sections` })}
      ${tile({ label: 'Accepted', value: e.totals.accepted, sub: 'will become tasks', tone: 'ok' })}
      ${tile({
        label: 'Need a decision',
        value: e.totals.needDecision,
        sub: 'conditional, or unclear',
        tone: e.totals.needDecision ? 'caution' : 'ok',
      })}
      ${tile({ label: 'Not tasks', value: e.totals.references, sub: 'point at other manuals' })}
    </div>

    ${e.totals.unverified
      ? callout('exceed', '!', html`
          <b>${e.totals.unverified} quote(s) could not be found in the manual they claim to come from.</b>
          They are shown flagged and cannot be accepted without you reading them against the document.
        `)
      : ''}

    ${e.documents.some((d) => d.countMismatch)
      ? callout('caution', '!', html`
          <b>One of the manuals may not have been read in full.</b>
          ${e.documents.filter((d) => d.countMismatch).map((d) => html`<br>${esc(d.title)} — ${esc(d.countMismatch!)}`).join('')}
        `)
      : ''}

    ${e.totals.unknownHours
      ? callout('caution', '!', html`
          <b>${e.totals.unknownHours} task(s) depend on the aircraft's total time, which is not on file.</b>
          The manuals were read against ${fmtHours(e.readAgainst.tsnHours ?? 0)} h. A first-inspection task cannot be
          told to be behind or ahead of the aircraft from that, so those are left for you to decide.
        `)
      : ''}

    ${fold({
      key: 'read',
      title: 'What was read',
      sub: 'The quote check is what makes the rest checkable rather than merely plausible',
      body: html`
        <div class="rows">
          ${e.documents.map((d) => html`
            <div class="row" style="border-left-color:var(--${d.countMismatch ? 'caution' : 'ok'})">
              <div class="bd">
                <div class="t">${esc(d.title)} <span class="sub2">${esc(d.role.toLowerCase())}</span></div>
                <div class="m tiny dim">
                  ${d.pages ?? '?'} pages · ${d.sectionsFound} section(s) · ${d.tasksFound} task(s)
                  ${d.verifiedPercent !== null ? ` · ${d.verifiedPercent}% of quotes found in the document` : ' · nothing to check'}
                </div>
              </div>
            </div>
          `).join('')}
        </div>
      `,
    })}

    ${e.sections.map((s) => {
      const decided = s.tasks.filter((t) => t.decision === 'ACCEPTED').length;
      return fold({
        key: s.key,
        title: esc(s.title),
        sub: html`
          <span class="mono">${esc(s.code)}</span> · ${esc(sectionInterval(s))}
          ${s.isFirstOnly ? ' · one-off, not a repeat' : ''} ·
          ${s.tasks.length} task(s), ${decided} accepted
        `,
        actions: approved ? '' : html`
          <div style="display:flex;gap:6px">
            <button class="btn sm" data-section-accept="${esc(s.code)}">Accept all that apply</button>
            <button class="btn sm" data-section-reject="${esc(s.code)}">Reject all</button>
          </div>
        `,
        body: html`<div>${s.tasks.map((t) => taskRow(t)).join('')}</div>`,
      });
    }).join('')}

    ${e.gaps.length
      ? fold({
          key: 'gaps',
          title: 'What these manuals do not contain',
          sub: 'A programme built from these alone is incomplete — said here rather than discovered later',
          body: html`
            <div class="rows">
              ${e.gaps.map((g) => html`
                <div class="row" style="border-left-color:var(--caution)">
                  <div class="bd"><div class="t">${esc(g)}</div>
                    <div class="m tiny dim">Referred to by a task, but not one of the manuals read.</div></div>
                </div>
              `).join('')}
            </div>
          `,
        })
      : ''}
  `;
}

// ── dialogs ─────────────────────────────────────────────────────────────────

function editTask(aircraftId: string, e: AmpExtraction, t: AmpTask, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Correct what was read',
    sub: `${t.fromDocument ?? 'manual'}${t.pageNumber ? ` · page ${t.pageNumber}` : ''}`,
    primary: 'Save the correction',
    body: () => html`
      ${field('Task', input('title', t.title, 'maxlength="300"'))}
      ${callout('info', 'i', html`
        <b>What the manual says:</b><br>
        <span style="font-size:12.5px">${esc(t.verbatim)}</span>
      `)}
      ${field('Why it was changed', input('note', t.decisionNote ?? '', 'placeholder="Optional — for the next reader" maxlength="400"'))}
      <p class="tiny dim" style="margin-top:8px">
        The quote never changes. Correcting the wording of a task does not change what it was read from,
        so the citation still points at the same page.
      </p>
    `,
    collect: (root) => {
      const title = val(root, 'title');
      if (!title) return { error: 'A task needs a title.' };
      return { decision: t.decision === 'REJECTED' ? 'PROPOSED' : t.decision, title, note: val(root, 'note') || undefined };
    },
    submit: (body) => api.ampExtraction.decide(aircraftId, e.id, t.id, body),
    done: () => 'Corrected',
    onDone: reload,
  });
}

function approveDialog(aircraftId: string, e: AmpExtraction, people: Array<{ id: string; displayName: string }>, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Approve as the aircraft’s programme',
    sub: 'ML.A.302 — this is the act that makes it the programme',
    primary: `Approve ${e.totals.accepted} task(s)`,
    body: () => html`
      <div class="frow">
        ${field('Approved by', select('approvedByPersonId', people.map((p) => opt(p.id, p.displayName)).join('')),
          'Must hold AMP_APPROVE')}
        ${field('Basis', select('basis', [
          opt('ML_APPROVED_BY_ORG', 'Indirect approval by the organisation — ML.A.302(b)(1)'),
          opt('ML_DECLARED', 'Declared by the owner — ML.A.302(b)(2)'),
          opt('ML_MIP', 'Minimum inspection programme — ML.A.302(d)'),
          opt('M_APPROVED', 'Approved under Part-M'),
        ].join('')))}
      </div>
      ${field('Note', input('note', '', 'placeholder="Optional — kept on the programme" maxlength="400"'))}
      ${callout('caution', '!', html`
        <b>${e.totals.accepted} of ${e.totals.proposed} proposed tasks are accepted.</b>
        ${e.totals.references} are references to other manuals and cannot be approved;
        ${e.totals.needDecision} are still undecided and will not be included.
        <br><br>
        This writes a new revision. Every task keeps the manual, page and quote it came from.
      `)}
      ${e.currentProgramme
        ? callout('info', 'i', html`
            <b>Added to revision ${esc(e.currentProgramme.revision)}, not instead of it.</b>
            Its ${e.currentProgramme.taskCount} task(s) move onto the new revision with their compliance
            history intact${e.totals.alreadyOnProgramme
              ? html`, and the ${e.totals.alreadyOnProgramme} accepted task(s) already on the programme
                     stay as they are rather than being entered twice`
              : ''}.
          `)
        : ''}
    `,
    collect: (root) => {
      const approvedByPersonId = val(root, 'approvedByPersonId');
      if (!approvedByPersonId) return { error: 'Who is approving it?' };
      return { approvedByPersonId, basis: val(root, 'basis'), note: val(root, 'note') || undefined };
    },
    submit: (body) => api.ampExtraction.approve(aircraftId, e.id, body),
    done: (r) =>
      `${r.registration} — programme revision ${r.revision}: ${r.totalTasks} task(s), ` +
      `${r.tasksCreated} new, ${r.tasksCarriedForward} carried forward` +
      (r.intervalChanged.length ? ` · ${r.intervalChanged.length} now on it at two intervals — check the programme` : ''),
    onDone: reload,
  });
}

// ── entry point ─────────────────────────────────────────────────────────────

/**
 * Whether the screen is showing the last reading or the manual picker.
 *
 * A reading that had been approved took the screen over permanently: the
 * button on the programme tab said "Build from the manuals" and landed on the
 * review of the reading before it, with nothing to tick and no way through. A
 * second manual — the engine one, uploaded a month after the airframe set —
 * could not be read at all.
 */
let picking = false;

/**
 * The poll that follows a reading in progress.
 *
 * Held here rather than in the render so that leaving the screen stops it: a
 * timer left running against a host element that is no longer on the page
 * would go on fetching for as long as the tab is open.
 */
let watching: ReturnType<typeof setTimeout> | null = null;

const stopWatching = () => {
  if (watching) clearTimeout(watching);
  watching = null;
};

export async function renderAmpExtraction(host: HTMLElement, aircraftId: string): Promise<void> {
  stopWatching();
  host.innerHTML = loading('Loading the manuals');
  try {
    const existing = await api.ampExtraction.latest(aircraftId);
    const reload = () => void renderAmpExtraction(host, aircraftId);

    // A reading that is ready or approved is the thing to show — unless the
    // reader has asked for the picker, which is how another manual is read.
    if (!picking && existing && ['READY', 'APPROVED'].includes(existing.state)) {
      host.innerHTML = reviewView(existing);
      bindReview(host, aircraftId, existing, reload);
      return;
    }
    picking = false;

    const candidates = await api.ampExtraction.candidates(aircraftId);
    host.innerHTML = pickerView(candidates, existing);

    // Abandoning, from wherever the button happens to be — the callout is
    // rewritten under it while a reading runs, so the click is caught on the
    // page rather than bound to a button that gets replaced.
    host.addEventListener('click', async (ev) => {
      if (!(ev.target as HTMLElement).closest('[data-abandon]')) return;
      const running = await api.ampExtraction.latest(aircraftId);
      if (!running) return reload();
      try {
        await api.ampExtraction.abandon(aircraftId, running.id, 'Cleared from the start screen.');
        toast('Cleared. You can read the manuals again.');
      } catch (err) {
        // Already gone is the outcome that was wanted, not an error worth showing.
        if (!/already/i.test(err instanceof Error ? err.message : '')) {
          toast(err instanceof Error ? err.message : 'That could not be cleared.', 'error');
        }
      }
      reload();
    });

    // While something is being read, ask again in a few seconds — and rewrite
    // only the paragraph that changes.
    //
    // Re-rendering the whole screen every six seconds is what a reader sees as
    // a flicker: the manual list is rebuilt, the ticks are re-applied and the
    // scroll position moves under them, all to change "manual 2 of 5" to
    // "manual 3 of 5". The full render is kept for the one refresh that
    // matters — when the reading stops being a reading.
    if (existing && ['PENDING', 'READING'].includes(existing.state)) {
      const tick = async () => {
        if (!host.isConnected) return;
        try {
          const now = await api.ampExtraction.latest(aircraftId);
          if (!now || !['PENDING', 'READING'].includes(now.state)) return reload();
          const box = host.querySelector('[data-reading]');
          if (box) box.innerHTML = readingCallout(now);
        } catch {
          // A poll that fails is not worth a message: the next one is six
          // seconds away, and the reading is unaffected either way.
        }
        watching = setTimeout(tick, 6_000);
      };
      watching = setTimeout(tick, 6_000);
    }

    host.querySelector('[data-start]')?.addEventListener('click', async () => {
      const picked = [...host.querySelectorAll<HTMLInputElement>('[data-doc-pick]')]
        .filter((b) => b.checked)
        .map((b) => b.dataset.docPick!);
      if (!picked.length) return toast('Tick at least one manual to read.', 'error');

      const button = host.querySelector<HTMLButtonElement>('[data-start]')!;
      button.disabled = true;
      button.textContent = 'Starting…';
      try {
        // Returns as soon as the reading has been started, not when it is
        // done. What happens next is shown by the poll above.
        const started = await api.ampExtraction.start(aircraftId, { documentIds: picked });
        toast(`Reading ${started.documents} manual(s) — this page will show the proposal when it is done.`);
        reload();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'The manuals could not be read.', 'error');
        button.disabled = false;
        button.textContent = 'Read the ticked manuals';
      }
    });
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load this screen.');
  }
}

function bindReview(host: HTMLElement, aircraftId: string, e: AmpExtraction, reload: () => void): void {
  const all = e.sections.flatMap((s) => s.tasks);
  const find = (id: string) => all.find((t) => t.id === id);

  host.querySelectorAll<HTMLInputElement>('[data-accept]').forEach((box) =>
    box.addEventListener('change', async () => {
      const id = box.dataset.accept!;
      try {
        await api.ampExtraction.decide(aircraftId, e.id, id, {
          decision: box.checked ? 'ACCEPTED' : 'PROPOSED',
        });
        // Only the row's own look changes; repainting the page here would lose
        // the reviewer's place in two hundred tasks.
        const row = box.closest('.trow');
        row?.classList.toggle('is-review', !box.checked);
        row?.classList.remove('is-rejected');
      } catch (err) {
        box.checked = !box.checked;
        toast(err instanceof Error ? err.message : 'That could not be recorded.', 'error');
      }
    }),
  );

  host.querySelectorAll<HTMLElement>('[data-reject]').forEach((b) =>
    b.addEventListener('click', async () => {
      const t = find(b.dataset.reject!);
      if (!t) return;
      try {
        await api.ampExtraction.decide(aircraftId, e.id, t.id, {
          decision: t.decision === 'REJECTED' ? 'PROPOSED' : 'REJECTED',
        });
        reload();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'That could not be recorded.', 'error');
      }
    }),
  );

  host.querySelectorAll<HTMLElement>('[data-edit]').forEach((b) =>
    b.addEventListener('click', () => {
      const t = find(b.dataset.edit!);
      if (t) editTask(aircraftId, e, t, reload);
    }),
  );

  const section = async (code: string, decision: string) => {
    try {
      await api.ampExtraction.decideSection(aircraftId, e.id, code, decision);
      reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'That could not be recorded.', 'error');
    }
  };
  // What is open survives the next render — see initialiseFolds.
  host.querySelectorAll<HTMLDetailsElement>('[data-fold]').forEach((d) =>
    d.addEventListener('toggle', () => {
      const key = d.dataset.fold!;
      if (d.open) openFolds.add(key);
      else openFolds.delete(key);
    }),
  );

  // These buttons sit in the fold's summary, and a click on a summary opens or
  // shuts it. Preventing the default is what keeps "accept all" from also
  // closing the section it just accepted.
  const inSummary = (ev: Event) => {
    ev.preventDefault();
    ev.stopPropagation();
  };
  host.querySelectorAll<HTMLElement>('[data-section-accept]').forEach((b) =>
    b.addEventListener('click', (ev) => {
      inSummary(ev);
      void section(b.dataset.sectionAccept!, 'ACCEPTED');
    }));
  host.querySelectorAll<HTMLElement>('[data-section-reject]').forEach((b) =>
    b.addEventListener('click', (ev) => {
      inSummary(ev);
      void section(b.dataset.sectionReject!, 'REJECTED');
    }));

  // The citation. This is the whole point of the screen: the manual opens at
  // the page the task was read from, so review is checking rather than trusting.
  host.querySelectorAll<HTMLElement>('[data-cite]').forEach((a) =>
    a.addEventListener('click', (ev) => {
      ev.preventDefault();
      openPdfViewer({
        path: `/attachments/${a.dataset.cite}/download#page=${a.dataset.page}`,
        title: a.dataset.doc ?? 'Manual',
        subtitle: `Page ${a.dataset.page} — the task was read from this page`,
        accessToken: getAccessToken,
      });
    }),
  );

  // Back to the picker, which is where a manual uploaded since this reading is
  // read. An approved reading is finished business and simply steps aside; an
  // unapproved one is somebody's afternoon of review, so it says so first.
  host.querySelector('[data-pick]')?.addEventListener('click', () => {
    if (e.state === 'APPROVED') {
      picking = true;
      return reload();
    }
    openDialog<Record<string, unknown>>({
      title: 'Read other manuals',
      sub: 'This proposal has not been approved yet',
      primary: 'Leave it and choose manuals',
      body: () => callout('caution', '!', html`
        ${e.totals.accepted} task(s) accepted and ${e.totals.needDecision} still undecided stay as they
        are — the proposal is not abandoned. Starting a new reading replaces it as the latest one, and
        this review is then no longer reachable from here.
      `),
      collect: () => ({}),
      submit: async () => ({ ok: true }),
      done: () => 'Choose the manuals to read',
      onDone: () => {
        picking = true;
        reload();
      },
    });
  });

  host.querySelector('[data-abandon]')?.addEventListener('click', () => {
    openDialog<Record<string, unknown>>({
      title: 'Abandon this reading',
      sub: 'The proposal is kept but nothing is written to the programme',
      primary: 'Abandon it',
      body: () => field('Why', input('reason', '', 'maxlength="400"')),
      collect: (root) => {
        const reason = val(root, 'reason');
        return reason ? { reason } : { error: 'Say why it was abandoned.' };
      },
      submit: (body) => api.ampExtraction.abandon(aircraftId, e.id, String(body.reason)),
      done: () => 'Abandoned',
      onDone: reload,
    });
  });

  host.querySelector('[data-approve]')?.addEventListener('click', async () => {
    if (!e.totals.accepted) return toast('Nothing is accepted, so there is no programme to approve.', 'error');
    const org = await api.organisation.overview();
    const eligible = org.people.filter((p) => p.authorisations.some((a) => a.kind === 'AMP_APPROVE'));
    if (!eligible.length) {
      return toast('Nobody holds AMP_APPROVE. ML.A.302(b)(1) needs somebody who does.', 'error');
    }
    approveDialog(aircraftId, e, eligible.map((p) => ({ id: p.id, displayName: p.displayName })), reload);
  });
}
